import Vue from 'vue';
import CustomDecimals from '@/utils/CustomDecimals.js';
import PriceService from '@/utils/PriceService.js';
import StateUtils from '@/utils/StateUtils.js';

export default {
  namespaced: true,
  state: {
    activeWardrobeId: null,
    isCartCalculating: false,
    isCartCalculatingError: false,
    wardrobeList: []
  },
  mutations: {
    setActiveId (state, id) {
      state.activeWardrobeId = id;
    },
    upsertWardrobe (state, wardrobeData) {
      const index = state.wardrobeList.findIndex(w => w.id === wardrobeData.id);
      if (index !== -1) {
        state.wardrobeList.splice(index, 1, wardrobeData);
      } else {
        state.wardrobeList.push(wardrobeData);
      }
    },
    removeWardrobeFromList (state, idToRemove) {
      state.wardrobeList = state.wardrobeList.filter(w => w.id !== idToRemove);
    },
    clearActiveId (state) {
      state.activeWardrobeId = null;
    },
    setCartState (state, newState) {
      if (newState.wardrobeList && Array.isArray(newState.wardrobeList)) {
        state.wardrobeList = newState.wardrobeList;
      }

      if (newState.activeWardrobeId) {
        state.activeWardrobeId = newState.activeWardrobeId;
      } else {
        state.activeWardrobeId = null;
      }
    },
    updateWardrobePrice (state, { id, price }) {
      const wardrobe = state.wardrobeList.find(w => w.id === id);
      if (wardrobe) {
        Vue.set(wardrobe, 'price', { ...price });
      }
    },
    setCartCalculating (state, val) {
      state.isCartCalculating = val;
    },
    setCartCalculatingError (state, val) {
      state.isCartCalculatingError = val;
    }
  },
  getters: {
    wardrobeList: state => state.wardrobeList,
    activeWardrobeId: state => state.activeWardrobeId,
    activeWardrobeFromList: state => state.wardrobeList.find(item => item.id === state.activeWardrobeId),
    wardrobeIndex: (state) => (wardrobeId) => state.wardrobeList.findIndex(item => item.id === wardrobeId),
    cartTotalPrice: (state) => state.wardrobeList.reduce((sum, item) => {
      const priceSum = (item.price && item.price.total_price) ? item.price.total_price : 0;
      return sum + priceSum;
    }, 0),
    cartTotalPriceBeforeDiscount: (state) => state.wardrobeList.reduce((sum, item) => {
      const priceSum = (item.price && item.price.total_price_before_discount) ? item.price.total_price_before_discount : 0;
      return sum + priceSum;
    }, 0),
    isCartCalculating: state => state.isCartCalculating,
    isCartCalculatingError: state => state.isCartCalculatingError,
    isWardrobeReadyForCheckout: (state) => (wardrobeId) => {
      const wardrobe = state.wardrobeList.find(item => item.id === wardrobeId);

      if (!wardrobe) {
        return false;
      }

      const { creatorMode, stepsCompletion, screenshots } = wardrobe;
      const stepsForWardrobe = StateUtils.getStepsForCreatorMode(creatorMode);
      const isStepsCompleted = stepsForWardrobe.every(key => stepsCompletion[key] === true);

      if (!isStepsCompleted) {
        return false;
      }

      const shots = screenshots || {};

      const isDoorValid = !creatorMode.hasDoor || (!!shots && !!shots.materials);
      const isInventoryValid = !creatorMode.hasInventory || (!!shots && !!shots.front && !!shots.top);

      return isDoorValid && isInventoryValid;
    },
    isCartReadyForCheckout: (state, getters) => {
      if (state.wardrobeList.length === 0) {
        return false;
      }

      return state.wardrobeList.every(wardrobe => getters.isWardrobeReadyForCheckout(wardrobe.id));
    },
    invalidWardrobeIds: (state, getters) => state.wardrobeList
      .filter(wardrobe => !getters.isWardrobeReadyForCheckout(wardrobe.id))
      .map(wardrobe => wardrobe.id)
  },
  actions: {
    initNewWardrobe ({ state, commit, dispatch }) {
      let newWardrobeID = CustomDecimals.generateUUID();
      state.wardrobeList.push({ id: newWardrobeID });
      commit('setActiveId', newWardrobeID);
      dispatch('creator/resetCreator', newWardrobeID, { root: true });
    },
    syncCreatorToCart ({ state, commit, rootState }) {
      const currentId = state.activeWardrobeId;
      if (!currentId) {
        return;
      }

      const creatorData = JSON.parse(JSON.stringify(rootState.creator));
      if (creatorData.id !== currentId) {
        console.error('ID mismatch on cart save');
      }

      const wardrobeToSave = {
        id: currentId,
        ...creatorData
      };

      commit('upsertWardrobe', wardrobeToSave);
    },
    editExistingWardrobe ({ commit, dispatch, state }, idToEdit) {
      const wardrobe = state.wardrobeList.find(w => w.id === idToEdit);
      if (!wardrobe) {
        return;
      }

      commit('setActiveId', idToEdit);
      dispatch('creator/loadWardrobeForEditing', wardrobe, { root: true });
    },
    removeWardrobe ({ state, commit, dispatch }, idToRemove) {
      commit('removeWardrobeFromList', idToRemove);
      if (state.activeWardrobeId === idToRemove) {
        commit('clearActiveId');
        dispatch('creator/resetCreator', null, { root: true });
      }
    },

    async refreshAllCartPrices ({ state, commit }) {
      if (state.wardrobeList.length === 0) {
        return;
      }

      commit('setCartCalculating', true);
      commit('setCartCalculatingError', false);

      const promises = state.wardrobeList.map(wardrobeItem => {
        const payload = PriceService.buildPayload(
          wardrobeItem,
          {}
        );

        return PriceService.fetchPrice(payload)
          .then(price => {
            commit('updateWardrobePrice', { id: wardrobeItem.id, price });
          })
          .catch((err) => {
            console.error(`Error calculating price for wardrobe ${wardrobeItem.id}`, err);
            commit('setCartCalculatingError', true);
          });
      });

      await Promise.all(promises);
      commit('setCartCalculating', false);
    },
  }
};
